import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Dumbbell, User as UserIcon, Users, Calendar, BookmarkCheck, BookmarkPlus, Plus, Minus, ArrowDownUp } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FollowAlertsBell from "@/components/FollowAlertsBell";
import { useShowExpired, CalX, ExpiredEventsToggle } from "@/components/ExpiredToggle";

// Order used by the "By type" sort. Users are shown last.
const TYPE_ORDER = ["team", "trainer", "event", "user"];
const TYPE_META = {
    team: { label: "Teams", Icon: Users, color: "#ff4500" },
    trainer: { label: "Trainers", Icon: Dumbbell, color: "#2196f3" },
    event: { label: "Events", Icon: Calendar, color: "#22c55e" },
    user: { label: "Users", Icon: UserIcon, color: "#a855f7" },
};

const COLLAPSE_KEY = "tsi-following-collapsed";
const EVENT_SORT_KEY = "tsi-following-eventsort";   // "date" | "alpha"
const EVENT_ORDER_KEY = "tsi-following-eventorder"; // "asc" | "desc"

function loadCollapsed() {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY)) || {}; } catch { return {}; }
}

// Flatten the /following payload into a single normalized list.
function normalize(data) {
    const out = [];
    (data.teams || []).forEach((t) => out.push({ group: "team", targetType: "team", id: t.id, name: t.name || "Team", link: t.link, prefs: t.prefs }));
    (data.trainers || []).forEach((t) => out.push({ group: "trainer", targetType: "trainer", id: t.id, name: t.name || "Trainer", link: t.link, prefs: t.prefs }));
    (data.trainer_listings || []).forEach((t) => out.push({ group: "trainer", targetType: "trainer_listing", id: t.id, name: t.name || "Trainer listing", link: t.link, prefs: t.prefs }));
    (data.events || []).forEach((e) => out.push({ group: "event", targetType: "event", id: e.id, name: e.name || "Event", link: e.link, prefs: e.prefs, date: e.event_date || e.date || "" }));
    (data.users || []).forEach((u) => out.push({ group: "user", targetType: "user", id: u.id, name: u.username ? `${u.username}` : (u.name || "User"), link: u.link, prefs: u.prefs }));
    return out;
}

const byName = (a, b) => a.name.toLowerCase().replace(/^@/, "").localeCompare(b.name.toLowerCase().replace(/^@/, ""));
const byDate = (a, b) => (a.date || "").localeCompare(b.date || "");

function fmtEventDate(d) {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return `${dt.toLocaleDateString(undefined, { month: "short" }).toUpperCase()} ${dt.getUTCDate()} ${dt.getUTCFullYear()}`;
}
function isPastDate(d) {
    if (!d) return false;
    const dt = new Date(d);
    if (isNaN(dt)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dt < today;
}

export default function FollowingDrawer({ open, onClose }) {
    const { toggleFollow } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("alpha"); // "alpha" | "type"
    const [busy, setBusy] = useState(null);
    const [followState, setFollowState] = useState({}); // key -> bool; missing = following
    const [collapsed, setCollapsed] = useState(loadCollapsed);
    const [eventSort, setEventSort] = useState(() => localStorage.getItem(EVENT_SORT_KEY) || "date");
    const [eventOrder, setEventOrder] = useState(() => localStorage.getItem(EVENT_ORDER_KEY) || "asc");
    const [showExpired, setShowExpired] = useShowExpired();

    useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch { /* ignore */ } }, [collapsed]);
    useEffect(() => { try { localStorage.setItem(EVENT_SORT_KEY, eventSort); } catch { /* ignore */ } }, [eventSort]);
    useEffect(() => { try { localStorage.setItem(EVENT_ORDER_KEY, eventOrder); } catch { /* ignore */ } }, [eventOrder]);

    const toggleCollapse = (g) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/following");
            setItems(normalize(data));
            setFollowState({});
        } catch {
            setItems([]);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { if (open) load(); }, [open, load]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const toggle = async (item) => {
        const key = `${item.targetType}:${item.id}`;
        const wasFollowing = followState[key] !== false;
        setBusy(key);
        const res = await toggleFollow(item.targetType, item.id);
        setBusy(null);
        if (res && res.ok !== false) {
            setFollowState((s) => ({ ...s, [key]: !wasFollowing }));
            toast.success(wasFollowing ? `Unfollowed ${item.name}` : `Following ${item.name}`);
        } else {
            toast.error("Could not update");
        }
    };

    // Build the render sections.
    const visibleItems = items.filter((it) => !(it.group === "event" && !showExpired && isPastDate(it.date)));
    let sections;
    if (sort === "alpha") {
        sections = [{ key: "all", group: null, label: null, rows: [...visibleItems].sort(byName) }];
    } else {
        sections = TYPE_ORDER
            .map((g) => {
                let rows = visibleItems.filter((x) => x.group === g);
                if (g === "event") {
                    rows = [...rows].sort(eventSort === "date" ? byDate : byName);
                    if (eventOrder === "desc") rows.reverse();
                } else {
                    rows = [...rows].sort(byName);
                }
                return { key: g, group: g, label: TYPE_META[g].label, rows };
            })
            .filter((s) => s.rows.length > 0);
    }

    const Row = ({ item }) => {
        const meta = TYPE_META[item.group];
        const Icon = meta.Icon;
        const key = `${item.targetType}:${item.id}`;
        const following = followState[key] !== false;
        const pastEvent = item.group === "event" && isPastDate(item.date);
        return (
            <div className="flex items-center gap-2 py-2 pr-1" data-testid={`following-row-${item.id}`}>
                {pastEvent ? <CalX size={16} /> : <Icon className="h-4 w-4 shrink-0" style={{ color: meta.color }} />}
                <div className="flex-1 min-w-0">
                    <Link
                        to={item.link || "/"}
                        onClick={onClose}
                        data-testid={`following-name-${item.id}`}
                        className="block truncate text-sm text-white hover:text-[#ff4500] transition-colors"
                    >
                        {item.name}
                    </Link>
                    {item.group === "event" && item.date && (
                        <div data-testid={`following-event-date-${item.id}`} className={`text-[9px] uppercase tracking-[0.15em] leading-tight ${isPastDate(item.date) ? "text-[#ef4444]" : "text-[#777]"}`}>
                            {fmtEventDate(item.date)}
                        </div>
                    )}
                </div>
                <FollowAlertsBell
                    targetType={item.targetType}
                    targetId={item.id}
                    initialPrefs={item.prefs || {}}
                    size={15}
                    align="end"
                    triggerClassName="inline-flex items-center justify-center h-8 w-8 shrink-0 border border-[#2a2a2a] bg-[#0f0f0f] text-[#a0a0a0] hover:text-white transition-colors"
                />
                <button
                    type="button"
                    data-testid={`following-unfollow-${item.id}`}
                    title={following ? "Following — tap to unfollow" : "Unfollowed — tap to follow"}
                    aria-label={following ? `Unfollow ${item.name}` : `Follow ${item.name}`}
                    disabled={busy === key}
                    onClick={() => toggle(item)}
                    className="inline-flex items-center justify-center h-8 w-8 shrink-0 border border-[#2a2a2a] bg-[#0f0f0f] text-[#a0a0a0] hover:text-white transition-colors disabled:opacity-40"
                >
                    {following ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                </button>
            </div>
        );
    };

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                data-testid="following-overlay"
                className={`fixed inset-0 z-[1190] bg-black/60 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            />
            {/* Panel */}
            <aside
                data-testid="following-drawer"
                className={`fixed top-0 left-0 z-[1200] h-full w-[86vw] max-w-sm md:w-80 bg-[#0d0d0d] border-r border-[#2a2a2a] shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
            >
                <div className="flex items-center justify-between px-4 py-4 border-b border-[#2a2a2a]">
                    <h2 className="font-display text-xl tracking-wide">Following{items.length ? ` · ${items.length}` : ""}</h2>
                    <button type="button" onClick={onClose} data-testid="following-close" aria-label="Close" className="inline-flex items-center justify-center h-8 w-8 text-[#a0a0a0] hover:text-white transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Sort + expired-events toggle */}
                <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center gap-3">
                    <div className="inline-flex border border-[#2a2a2a]" data-testid="following-sort">
                        <button type="button" data-testid="following-sort-alpha" onClick={() => setSort("alpha")} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${sort === "alpha" ? "bg-[#ff4500] text-white" : "text-[#a0a0a0] hover:text-white"}`}>A–Z</button>
                        <button type="button" data-testid="following-sort-type" onClick={() => setSort("type")} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${sort === "type" ? "bg-[#ff4500] text-white" : "text-[#a0a0a0] hover:text-white"}`}>By type</button>
                    </div>
                    <ExpiredEventsToggle showExpired={showExpired} onToggle={() => setShowExpired((v) => !v)} testid="following-toggle-expired" />
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-2" data-testid="following-list">
                    {loading ? (
                        <div className="text-sm text-[#a0a0a0] py-8 text-center">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="text-sm text-[#a0a0a0] py-10 text-center">You're not following anyone yet. Follow teams, trainers, events and people to see them here.</div>
                    ) : (
                        sections.map((sec) => {
                            const isCollapsed = sec.group && collapsed[sec.group];
                            return (
                                <div key={sec.key} className="mb-2">
                                    {sec.label && (
                                        <div className="flex items-center justify-between gap-2 mt-3 mb-1">
                                            <button
                                                type="button"
                                                onClick={() => toggleCollapse(sec.group)}
                                                data-testid={`following-group-toggle-${sec.group}`}
                                                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#888] hover:text-white transition-colors"
                                            >
                                                {isCollapsed ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                                                {sec.label} <span className="text-[#555]">· {sec.rows.length}</span>
                                            </button>
                                            {sec.group === "event" && !isCollapsed && (
                                                <div className="flex items-center gap-1.5">
                                                    <div className="inline-flex border border-[#2a2a2a]" data-testid="following-event-sort">
                                                        <button type="button" data-testid="following-event-sort-date" onClick={() => setEventSort("date")} className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] ${eventSort === "date" ? "bg-[#22c55e] text-black" : "text-[#a0a0a0] hover:text-white"}`}>Date</button>
                                                        <button type="button" data-testid="following-event-sort-alpha" onClick={() => setEventSort("alpha")} className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] ${eventSort === "alpha" ? "bg-[#22c55e] text-black" : "text-[#a0a0a0] hover:text-white"}`}>A–Z</button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        data-testid="following-event-order"
                                                        onClick={() => setEventOrder((o) => (o === "asc" ? "desc" : "asc"))}
                                                        title={eventSort === "date" ? (eventOrder === "asc" ? "Oldest first" : "Newest first") : (eventOrder === "asc" ? "A→Z" : "Z→A")}
                                                        className="inline-flex items-center justify-center h-6 w-6 border border-[#2a2a2a] text-[#a0a0a0] hover:text-white transition-colors"
                                                    >
                                                        <ArrowDownUp className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {!isCollapsed && (
                                        <div className="divide-y divide-[#1c1c1c]">
                                            {sec.rows.map((item) => <Row key={`${item.targetType}:${item.id}`} item={item} />)}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </aside>
        </>
    );
}
